import React, { FC, useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { useForm, FormProvider, Controller, ControllerRenderProps, useWatch } from 'react-hook-form';

import { Button, CheckBox, InputTextField, Types } from '@marriott/mi-ui-library';

import { useAuthStore, useLegalGenericStore, usePrivacyRightsStore } from '../../store';
import { REGEX_CONSTANTS } from '../../utils/constants';
import { usePostRequest } from '../../hooks/usePostRequest';

import { LegalTextWithLink } from '../../molecules/LegalTextWithLink';
import { PrivacyRightsModal } from '../../molecules/PrivacyRightsComponent';

import { PRIVACY_RIGHTS_API_ENDPOINT } from './constant';
import { StyledPrivacyRightsFormSection } from './PrivacyRightsWrapper.styles';
import {
  getCheckboxFieldName,
  getConsentsForPayload,
  getMasterChildren,
  getNormalizedVariants,
  getParentMaster,
  getResolvedConsents,
  updateConsentsToOneTrustCookie,
} from './utils/PrivacyRightsUtils';
import {
  ConsentVariant,
  PhoenixLegalUpdatePrivacyConsentResult,
  PrivacyRightsCheckboxFormValues,
  PrivacyRightsFormDataType,
  PrivacyRightsFormProps,
} from './PrivacyRightsWrapper.types';

export const PrivacyRightsFormSection: FC<PrivacyRightsFormProps> = ({
  formData,
  proceedModalData,
  privacyRightsMainComponentStates,
  refreshConsents,
  currentConsentsStatus,
}) => {
  const { isAuthenticated: isAuth } = useAuthStore();
  const { sessionData } = useLegalGenericStore();
  const { uniqueId, email, checkboxes, cta, checkboxTitle } = formData;
  const { selectedStateOrLoggedInUserStateCode, isUsStateAndBelongToCpraGroups } = privacyRightsMainComponentStates;
  const [showOptInConfirmationModal, setShowOptInConfirmationModal] = useState(false);

  const {
    confirmationModalGenericData,
    setConfirmationModalGenericData,
    setDoNotSellShareFormsInputFieldsPendingDataStatus,
    setAlertComponentConfig,
  } = usePrivacyRightsStore.getState();

  const { optInConfirmationModalHeader, modalProceedButtonLabel, modalCancelButtonLabel } =
    confirmationModalGenericData || {};

  const { AUTH_SUBMIT_ENDPOINT, UNAUTH_SUBMIT_ENDPOINT } = PRIVACY_RIGHTS_API_ENDPOINT;

  const defaultFormValues = {
    emailAddress: '',
    privacyConsents: {},
  };

  const methods = useForm<PrivacyRightsCheckboxFormValues>({
    mode: 'onSubmit',
    shouldUnregister: true,
    defaultValues: defaultFormValues,
  });

  const {
    control,
    formState: { errors },
    reset,
    setValue,
    getValues,
    handleSubmit,
  } = methods;

  const selectedCheckboxValues =
    useWatch({
      control,
      name: 'privacyConsents',
    }) || {};

  const handlePrivacyCheckboxChange = (variant: ConsentVariant, checked: boolean) => {
    const updateCheckboxValue = (checkboxVariant: ConsentVariant, value: boolean) => {
      setValue(getCheckboxFieldName(checkboxVariant), value, {
        shouldDirty: true,
        shouldTouch: true,
      });
    };

    // Update the checkbox that user actually clicked.
    updateCheckboxValue(variant, checked);

    setDoNotSellShareFormsInputFieldsPendingDataStatus({
      [uniqueId]: true,
    });

    // Check whether the clicked checkbox is a master checkbox.
    const childVariants = getMasterChildren(variant, checkboxes);

    if (childVariants.length > 0) {
      // Since a master checkbox was clicked, update all its child checkboxes
      childVariants.forEach(childVariant => {
        updateCheckboxValue(childVariant, checked);
      });

      return;
    }

    // If the clicked checkbox is not a master checkbox,
    const masterVariant = getParentMaster(variant);

    if (masterVariant) {
      const currentValues = getValues('privacyConsents') || {};
      const masterChildren = getMasterChildren(masterVariant, checkboxes);
      const shouldCheckMaster = masterChildren.every(childVariant =>
        childVariant === variant ? checked : Boolean(currentValues[childVariant])
      );
      updateCheckboxValue(masterVariant, shouldCheckMaster);
    }
  };

  const [updateUserPrivacyConsent, { data: privacyConsentApiResponse, loading: isSubmitApiCallIsInProgress }] =
    usePostRequest<PhoenixLegalUpdatePrivacyConsentResult, PrivacyRightsFormDataType>({
      onError(error) {
        console.error(`Something went wrong while submitting: ${JSON.stringify(error)}`);

        setAlertComponentConfig({
          alertType: 'apiError',
          showAlertComponent: true,
        });
      },
    });

  const { onSubmit, makeApiCall } = useMemo(() => {
    let formData: PrivacyRightsFormDataType | null = null;
    const privacyConsentSubmitApiEndPoint = isAuth ? AUTH_SUBMIT_ENDPOINT : UNAUTH_SUBMIT_ENDPOINT;

    return {
      onSubmit: async (data: PrivacyRightsCheckboxFormValues) => {
        const selectedConsents = getConsentsForPayload(data);

        formData = {
          input: {
            consents: [...selectedConsents],
            ...(isAuth
              ? { customerId: sessionData?.cacheData?.data?.consumerID }
              : {
                  email: {
                    emailAddress: data['emailAddress'],
                  },
                }),
          },
        };

        if (selectedConsents.length) {
          setShowOptInConfirmationModal(true);
          return;
        }
      },
      makeApiCall: () => {
        setShowOptInConfirmationModal(false);
        updateUserPrivacyConsent(privacyConsentSubmitApiEndPoint, formData!, true);
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  const closeOptInConfirmationModal = () => setShowOptInConfirmationModal(false);

  const emailChangeHandler = (
    field: ControllerRenderProps<PrivacyRightsCheckboxFormValues, 'emailAddress'>,
    value: string
  ) => {
    field.onChange({
      target: {
        name: field.name,
        value,
      },
    });

    setDoNotSellShareFormsInputFieldsPendingDataStatus({
      [uniqueId]: isAuth ? isAnyCheckboxSelected : isAnyCheckboxSelected || Boolean(value.trim()),
    });
  };

  useEffect(() => {
    if (!privacyConsentApiResponse) {
      return;
    }

    const responseCode = privacyConsentApiResponse.status?.[0]?.code;

    if (!responseCode) {
      setAlertComponentConfig({
        alertType: 'apiError',
        showAlertComponent: true,
      });
      return;
    }

    const isSuccessOrIneligible = responseCode === 'SUCCESS' || responseCode === 'INELIGIBLE';

    setAlertComponentConfig({
      alertType: isSuccessOrIneligible ? (isAuth ? 'authProfileUpdated' : 'profileUpdated') : 'profileNotFound',
      showAlertComponent: true,
    });

    if (responseCode === 'SUCCESS') {
      const currentValues = getValues('privacyConsents') ?? {};
      const updatedConsents: Partial<Record<ConsentVariant, boolean>> = {};
      Object.entries(currentValues).forEach(([variant, isSelected]) => {
        if (!isSelected) {
          return;
        }
        getNormalizedVariants(variant).forEach((normalizedVariant: ConsentVariant) => {
          updatedConsents[normalizedVariant] = true;
        });
      });
      const getSelectedPrivacyConsents = (consents: Partial<Record<ConsentVariant, boolean>>) => ({
        CCPA: consents['CCPA_IN'] ? true : consents['CCPA_OUT'] ? false : undefined,
        CPRA: consents['CPRA_IN'] ? true : consents['CPRA_OUT'] ? false : undefined,
      });
      const resolvedConsents = getResolvedConsents(currentConsentsStatus, getSelectedPrivacyConsents(updatedConsents));
      updateConsentsToOneTrustCookie(resolvedConsents);
      refreshConsents();
    }

    if (isSuccessOrIneligible) {
      if (!isAuth) {
        reset();
        setValue('emailAddress', '');
      }
      setDoNotSellShareFormsInputFieldsPendingDataStatus({ [uniqueId]: false });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privacyConsentApiResponse, isAuth, uniqueId]);

  useEffect(() => {
    reset();

    const {
      btnSubmitLabel: modalProceedButtonLabel,
      btnCancelLabel: modalCancelButtonLabel,
      confirmationHeader: optInConfirmationModalHeader,
    } = proceedModalData;

    setConfirmationModalGenericData({
      optInConfirmationModalHeader,
      modalProceedButtonLabel,
      modalCancelButtonLabel,
    });
    setDoNotSellShareFormsInputFieldsPendingDataStatus(null);

    return () => setDoNotSellShareFormsInputFieldsPendingDataStatus(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueId]);

  const showLoadingSpinnerOnButton: boolean = showOptInConfirmationModal || isSubmitApiCallIsInProgress;

  const isAnyCheckboxSelected = Object.values(selectedCheckboxValues).some(Boolean);

  const isAnyValidCPRAGroupStateSelected =
    isUsStateAndBelongToCpraGroups && selectedStateOrLoggedInUserStateCode !== 'NONE' && checkboxes.length > 0;

  return (
    <StyledPrivacyRightsFormSection>
      {isAnyValidCPRAGroupStateSelected && (
        <FormProvider {...methods}>
          <form key={`privacy-rights-form-${uniqueId}`} onSubmit={handleSubmit(onSubmit)} data-uniqueid={uniqueId}>
            <div className="form-container mb-4">
              <div className={clsx('form-element')} data-testid={`legal-privacyRights-email`}>
                {email && (
                  <Controller
                    name="emailAddress"
                    control={control}
                    rules={{
                      required: 'Please enter valid email',
                      pattern: {
                        value: REGEX_CONSTANTS.EMAIL_REGEX,
                        message: 'Please enter valid email',
                      },
                    }}
                    defaultValue=""
                    render={({ field }) => (
                      <InputTextField
                        {...field}
                        className="m-input-text-field"
                        label={email.placeholder}
                        type="text"
                        testId="emailAddress"
                        inputValue={field.value}
                        showErrorMessage={!!errors?.['emailAddress']}
                        messageToShow={'Please enter valid email'}
                        inputMaxLength={50}
                        infoLabel="emailAddress"
                        getInputValue={value => emailChangeHandler(field, value)}
                        getInputProps={() => ({
                          autoComplete: 'off',
                          name: 'emailAddress',
                        })}
                      />
                    )}
                  />
                )}
                <span className="t-subtitle-l mt-4">{checkboxTitle}</span>
                <div className="spacing-box-wrapper">
                  <div className="spacing-box">
                    {checkboxes.map(item => (
                      <Controller
                        key={`${item.uniqueId}`}
                        name={`privacyConsents.${item.variant}`}
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <CheckBox
                            checkboxId={`privacy-rights-checkbox-${item.uniqueId}`}
                            checkboxName={field.name}
                            checked={Boolean(field.value)}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              handlePrivacyCheckboxChange(item.variant, event.target.checked);
                            }}
                          >
                            <LegalTextWithLink
                              className={` ${item.variant.includes('MASTER') ? 't-label-alt-s' : 't-font-s'}`}
                              descriptionText={item.checkboxLabel}
                            />
                          </CheckBox>
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <Button
              className={clsx('m-button-primary sbt-btn', {
                'disable-btn': !isAnyCheckboxSelected || showLoadingSpinnerOnButton,
              })}
              type={Types.ButtonTypeVariation.Submit}
              isDisabled={!isAnyCheckboxSelected || showLoadingSpinnerOnButton}
              testId={`legal-privacyRights-submit-button`}
            >
              {showLoadingSpinnerOnButton ? <div className="m-spinner-s" /> : cta?.submitBtnLabel}
            </Button>
          </form>
        </FormProvider>
      )}
      {showOptInConfirmationModal && (
        <PrivacyRightsModal
          showModal={showOptInConfirmationModal}
          labelledBy="submit-modal-title"
          onClose={closeOptInConfirmationModal}
          modalHeaderContent={optInConfirmationModalHeader!}
          modalProceedButtonLabel={modalProceedButtonLabel!}
          modalCancelButtonLabel={modalCancelButtonLabel!}
          onProceed={makeApiCall}
        />
      )}
    </StyledPrivacyRightsFormSection>
  );
};
